<template>
  <div class="product-page">

    <!-- Page header -->
    <div class="page-header">
      <button class="back-btn" onclick="window.history.back()"><i class="ft-arrow-left"></i></button>
      <div class="header-info">
        <span class="page-title">{{ productTitle || 'Meal Detail' }}</span>
        <span class="page-subtitle" v-if="free_issue && productPrice">R{{ productPrice }}</span>
      </div>
      <router-link to="/checkout" class="cart-btn">
        <i class="ft-shopping-cart"></i>
      </router-link>
    </div>

    <div class="content-wrap">

      <!-- Meal image -->
      <div class="meal-image-wrap">
        <img v-if="mealImageUrl" :src="mealImageUrl" class="meal-image" alt="Meal image" />
        <div v-else class="meal-image-placeholder" v-html="productIconSvg"></div>
        <div class="meal-name-overlay">
          <span>{{ productTitle }}</span>
          <span class="price-tag" v-if="free_issue && productPrice">R{{ productPrice }}</span>
        </div>
      </div>

      <!-- Info tabs -->
      <div class="info-tabs-wrap">
        <div class="tab-bar">
          <button class="tab-btn" :class="{ 'tab-btn--active': activeTab === 'description' }" @click="activeTab = 'description'">Description</button>
          <button class="tab-btn" :class="{ 'tab-btn--active': activeTab === 'nutritional' }" @click="activeTab = 'nutritional'">Nutritional</button>
          <button class="tab-btn" :class="{ 'tab-btn--active': activeTab === 'allergies' }" @click="activeTab = 'allergies'">Allergies</button>
        </div>

        <div class="tab-content-wrap">
          <p class="tab-content-text" v-if="activeTab === 'description'">
            {{ productDescription || 'No description available.' }}
          </p>
          <p class="tab-content-text" v-if="activeTab === 'nutritional'">
            {{ productNutrition || 'No nutritional information available.' }}
          </p>
          <p class="tab-content-text" v-if="activeTab === 'allergies'">
            {{ productAllergies || 'No recorded allergies.' }}
          </p>
        </div>
      </div>

      <!-- Additions -->
      <div class="options-section" v-if="inclusions && inclusions.length > 0">
        <p class="options-section-label">
          <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          Additions
        </p>

        <div class="option-group" v-for="(inclusion, index) in inclusions" :key="inclusion.id || index">
          <div class="option-group-header">
            <span class="option-group-name">{{ inclusion.addition_type }}</span>
            <span class="option-group-hint">
              {{ inclusion.addition_input_type === 'single' ? 'Select one' : 'Select multiple' }}
              <span class="required-chip" v-if="inclusion.required === 'yes'">Required</span>
            </span>
          </div>

          <!-- Single select -->
          <div class="option-chips" v-if="inclusion.addition_input_type === 'single'" :id="'collapse-addition-' + index">
            <button
              v-for="addition in inclusion.additions"
              :key="addition.id"
              :id="'custom-block-' + addition.id"
              type="button"
              class="option-chip single-block custom-block"
            >{{ addition.label }}</button>
          </div>

          <!-- Multi select -->
          <div class="option-chips" v-if="inclusion.addition_input_type === 'multi'" :id="'collapse-addition-' + index">
            <button
              v-for="addition in inclusion.additions"
              :key="addition.id"
              :id="'custom-block-' + addition.id"
              type="button"
              class="option-chip multiple-block custom-block"
            >{{ addition.label }}</button>
          </div>
        </div>
      </div>

      <!-- Exclusions -->
      <div class="options-section" v-if="exclusions && exclusions.length > 0">
        <p class="options-section-label">
          <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="5" y1="12" x2="19" y2="12"/></svg>
          Exclusions
        </p>

        <div class="option-group" v-for="(exclusion, index) in exclusions" :key="exclusion.id || index">
          <div class="option-group-header">
            <span class="option-group-name">{{ exclusion.exclusion_type }}</span>
            <span class="option-group-hint">Select to remove</span>
          </div>
          <div class="option-chips" :id="'collapse-exclusion-' + index">
            <button
              v-for="item in exclusion.exclusions"
              :key="item.id"
              :id="'custom-block-' + item.id"
              type="button"
              class="option-chip option-chip--exclusion single-block custom-block"
            >{{ item.label }}</button>
          </div>
        </div>
      </div>

      <!-- Quantity -->
      <div class="qty-section" v-if="showqty">
        <label class="qty-label">
          <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>
          Quantity
        </label>
        <div class="qty-row">
          <button class="qty-stepper" type="button" @click="decrementQty">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="5" y1="12" x2="19" y2="12"/></svg>
          </button>
          <span class="qty-value">{{ quantity || 1 }}</span>
          <button class="qty-stepper" type="button" @click="incrementQty">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          </button>
        </div>
      </div>

      <!-- Add to cart -->
      <div class="add-to-cart-wrap">
        <button type="button" class="add-to-cart-btn" v-on:click="addToCart">
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/>
            <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
          </svg>
          Add to Cart
        </button>
      </div>

    </div>
  </div>
</template>

<script>
import swal from 'sweetalert'

export default {
  name: 'product',
  data: function () {
    return {
      productTitle: '',
      productPrice: 0,
      productDescription: '',
      productAllergies: '',
      productNutrition: '',
      inclusions: [],
      exclusions: [],
      showqty: this.showHideQty(),
      quantity: 1,
      free_issue: false,
      activeTab: 'description',
      mealImageUrl: null
    }
  },
  computed: {
    productIconSvg: function () {
      var a = 'xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"'
      var t = (this.productTitle || '').toLowerCase()

      if (/tea|coffee|rooibos|cocoa|milo|hot.?drink|beverage|juice|water|smoothie|shake/.test(t))
        return '<svg ' + a + '><path d="M18 8h1a4 4 0 0 1 0 8h-1"/><path d="M2 8h16v9a4 4 0 0 1-4 4H6a4 4 0 0 1-4-4V8z"/><line x1="6" y1="1" x2="6" y2="4"/><line x1="10" y1="1" x2="10" y2="4"/><line x1="14" y1="1" x2="14" y2="4"/></svg>'

      if (/\begg\b|omelette|scrambled|boiled|fried.?egg/.test(t))
        return '<svg ' + a + '><path d="M12 22c5.5 0 8-3.6 8-8.5C20 8.2 16.4 2 12 2S4 8.2 4 13.5C4 18.4 6.5 22 12 22z"/></svg>'

      if (/breakfast|cereal|porridge|oat|muesli|pancake|waffle|granola/.test(t))
        return '<svg ' + a + '><path d="M17 18a5 5 0 0 0-10 0"/><line x1="12" y1="2" x2="12" y2="9"/><line x1="4.22" y1="10.22" x2="5.64" y2="11.64"/><line x1="1" y1="18" x2="3" y2="18"/><line x1="21" y1="18" x2="23" y2="18"/><line x1="18.36" y1="11.64" x2="19.78" y2="10.22"/><line x1="23" y1="22" x2="1" y2="22"/><polyline points="8 6 12 2 16 6"/></svg>'

      if (/soup|broth|chowder|bisque|veloute|consomme|minestrone/.test(t))
        return '<svg ' + a + '><path d="M2 12h20"/><path d="M4 12c0 4.4 3.6 8 8 8s8-3.6 8-8"/><path d="M9 4c0 1.7-2 3-2 5"/><path d="M14 4c0 1.7-2 3-2 5"/></svg>'

      if (/fish|tuna|salmon|hake|sardine|prawn|shrimp|calamari|seafood/.test(t))
        return '<svg ' + a + '><path d="M6.5 12c-.5-2 1-5 5.5-5 5.5 0 9 3.5 9 5s-3.5 5-9 5c-4.5 0-6-3-5.5-5z"/><path d="M2 7l2.5 5L2 17"/><circle cx="17" cy="12" r="1"/></svg>'

      if (/sandwich|bread|roll|toast|wrap|roti|pita|bun|croissant|scone|muffin/.test(t))
        return '<svg ' + a + '><path d="M3 7c0-2.2 1.8-4 4-4h10c2.2 0 4 1.8 4 4v1c0 1.1-.9 2-2 2v8a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-8c-1.1 0-2-.9-2-2V7z"/></svg>'

      if (/\bfruit\b|apple|orange|banana|mango|grape|melon|berr/.test(t))
        return '<svg ' + a + '><path d="M12 20.94c1.5 0 2.75 1.06 4 1.06 3 0 6-8 6-12.22A4 4 0 0 0 17.66 6l-.64.14A4.76 4.76 0 0 1 12 6a4.76 4.76 0 0 1-5.02.14L6.34 6A4 4 0 0 0 2 9.78C2 14 5 22 8 22c1.25 0 2.5-1.06 4-1.06z"/><path d="M10 2c0 1.5 1 2.5 2 3"/></svg>'

      if (/salad|spinach|broccoli|vegetable|coleslaw|chakalaka|side.?salad/.test(t))
        return '<svg ' + a + '><path d="M2 22c4-4 8-12 18-14C19 18 12 22 2 22z"/><path d="M2 22 12 12"/></svg>'

      if (/dessert|cake|pudding|jelly|custard|tart|biscuit|cookie|sweet|chocolate|sponge|crumble|mousse|ice.?cream/.test(t))
        return '<svg ' + a + '><path d="M20 21v-8a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v8"/><path d="M4 16s.5-1 2-1 2.5 2 4 2 2.5-2 4-2 2 1 2 1"/><line x1="2" y1="21" x2="22" y2="21"/><path d="M7 8v3"/><path d="M12 8v3"/><path d="M17 8v3"/><path d="M7 4h.01"/><path d="M12 4h.01"/><path d="M17 4h.01"/></svg>'

      if (/\brice\b|pap|phuthu|pasta|spaghetti|noodle|potato|chips|maize/.test(t))
        return '<svg ' + a + '><path d="M3 11l19-9-9 19-2-8-8-2z"/></svg>'

      if (/special|chef/.test(t))
        return '<svg ' + a + '><circle cx="12" cy="8" r="6"/><path d="M15.477 12.89L17 22l-5-3-5 3 1.523-9.11"/></svg>'

      // default: fork & knife
      return '<svg ' + a + '><path d="M3 2v7c0 1.1.9 2 2 2h4a2 2 0 0 0 2-2V2"/><path d="M7 2v20"/><path d="M21 15V2a5 5 0 0 0-5 5v6c0 1.1.9 2 2 2h3zm0 0v7"/></svg>'
    }
  },
  beforeDestroy: function () {
    $('.food-header-pic').remove()
  },
  mounted: function () {
    this.$store.commit('SET_LAYOUT', 'AppLayout')
    $('.food-header-pic').remove()

    if (window.localStorage.getItem('free_issue') === 'Y') {
      this.free_issue = true
    }

    var meal = JSON.parse(window.localStorage.getItem('meal'))
    var inclusions = []
    var exclusions = []

    if (meal.additions) {
      meal.additions.forEach(function (addition) { inclusions.push(addition) })
    }
    if (meal.exclusions) {
      meal.exclusions.forEach(function (exclusion) { exclusions.push(exclusion) })
    }

    this.productTitle = meal.label
    this.productDescription = meal.description
    this.productAllergies = meal.allergies
    this.productNutrition = meal.nutritional_information
    this.inclusions = inclusions
    this.exclusions = exclusions
    this.productPrice = meal.price
    this.mealImageUrl = meal.image
      ? 'https://admin.medicater.co.za/assets/uploads/menu/' + meal.image
      : null

    if (!window.localStorage.getItem('cart') || window.localStorage.getItem('cart') === '') {
      window.localStorage.setItem('cart', JSON.stringify([]))
    }

    $(document).off('click', '.custom-block')
    $(document).on('click', '.custom-block', function (e) {
      e.preventDefault()
      if ($(this).hasClass('single-block')) {
        $(this).parent().find('.custom-block').removeClass('active')
        $(this).addClass('active')
      } else if ($(this).hasClass('multiple-block')) {
        $(this).toggleClass('active')
      }
      return false
    })
  },
  methods: {
    showHideQty: function () {
      var user = JSON.parse(window.localStorage.getItem('user'))
      if (window.localStorage.getItem('free_issue') === 'Y') {
        return user.merchant.show_free_issue_item_quantities === 'Y'
      }
      return user.merchant.show_order_item_quantities === 'Y'
    },
    incrementQty: function () {
      this.quantity = (this.quantity || 1) < 50 ? (this.quantity || 1) + 1 : 50
    },
    decrementQty: function () {
      this.quantity = (this.quantity || 1) > 1 ? (this.quantity || 1) - 1 : 1
    },
    addToCart: function () {
      var meal = JSON.parse(window.localStorage.getItem('meal'))
      var additions = []
      var exclusions = []
      var errors = ''

      if (meal.additions) {
        var index = 0
        meal.additions.forEach(function (addition) {
          if (addition.required === 'yes') {
            if ($('#collapse-addition-' + index + ' .active').length === 0) {
              errors += addition.addition_type + ' is required. '
            }
          }
          index++
        })
      }

      if (errors) {
        swal('Required', errors, 'error')
        return false
      }

      if (meal.additions) {
        meal.additions.forEach(function (addition) {
          addition.additions.forEach(function (customization) {
            if ($('#custom-block-' + customization.id).hasClass('active')) {
              additions.push({ id: customization.id, label: customization.label, description: customization.description, pos_plu: customization.pos_plu })
            }
          })
        })
      }

      if (meal.exclusions) {
        meal.exclusions.forEach(function (exclusion) {
          exclusion.exclusions.forEach(function (customization) {
            if ($('#custom-block-' + customization.id).hasClass('active')) {
              exclusions.push(customization)
            }
          })
        })
      }

      var period = JSON.parse(window.localStorage.getItem('period'))
      var cart = JSON.parse(window.localStorage.getItem('cart'))

      var categoryLimit = parseInt(meal.meal_category_order_limit, 10)
      if (categoryLimit) {
        var categoryQty = cart.reduce(function (sum, c) {
          if (c.category_id == meal.meal_category_id && c.period_id == period.id) {
            return sum + Number(c.quantity)
          }
          return sum
        }, 0)
        if (categoryQty + (this.quantity || 1) > categoryLimit) {
          swal('Limit Reached', 'You can only select ' + categoryLimit + ' item' + (categoryLimit === 1 ? '' : 's') + ' from this category per order.', 'error')
          return false
        }
      }


      var item = {
        mealId: meal.id,
        label: meal.label,
        price: meal.price,
        additions: additions,
        exclusions: exclusions,
        period_id: period.id,
        period_title: period.title,
        quantity: this.quantity || 1,
        category_id: meal.meal_category_id
      }

      cart.push(item)
      window.localStorage.setItem('cart', JSON.stringify(cart))

      window.localStorage.setItem('cart_added_toast', meal.label)
      this.$router.push('/preorder/' + this.$route.params.ward + '/' + this.$route.params.bed + '/' + this.$route.params.date + '/' + this.$route.params.period + '/categories/')
    }
  }
}
</script>

<style scoped>
.product-page {
  min-height: 100vh;
  background: #f0f4f8;
  padding-bottom: 40px;
}

/* ── Page header ── */
.page-header {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 30px 20px 20px;
  background: linear-gradient(135deg, #1a587b 0%, #0f3d5c 100%);
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 10;
  margin-top: 3.2rem;
  border-radius: 0;
  box-shadow: 0 8px 32px rgba(15, 61, 92, 0.35);
}

.back-btn {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: #fb5653;
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.15s;
}
.back-btn:hover { background: #e03e3b; }

.header-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
}

.page-title {
  font-size: 0.95rem;
  font-weight: 700;
  color: #fff;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.page-subtitle {
  font-size: 0.73rem;
  color: rgba(255, 255, 255, 0.7);
  font-weight: 600;
}

.cart-btn {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: #fff !important;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  text-decoration: none !important;
  flex-shrink: 0;
  transition: background 0.15s;
}
.cart-btn:hover { background: rgba(255, 255, 255, 0.25); }

/* ── Content ── */
.content-wrap {
  padding: 110px 16px 0;
  display: flex;
  flex-direction: column;
  gap: 14px;
  max-width: 680px;
  margin: 0 auto;
}

/* ── Meal image ── */
.meal-image-wrap {
  position: relative;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
}

.meal-image {
  width: 100%;
  height: 200px;
  object-fit: cover;
  display: block;
}

.meal-image-placeholder {
  width: 100%;
  height: 200px;
  background: linear-gradient(135deg, #1a587b 0%, #0f3d5c 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.35);
}

.meal-name-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(to top, rgba(0,0,0,0.75), transparent);
  padding: 32px 16px 14px;
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 8px;
  font-size: 0.95rem;
  font-weight: 700;
  color: #fff;
  line-height: 1.3;
}

.price-tag {
  background: #fb5653;
  color: #fff;
  font-size: 0.82rem;
  font-weight: 700;
  padding: 4px 10px;
  border-radius: 8px;
  flex-shrink: 0;
}

/* ── Info tabs ── */
.info-tabs-wrap {
  background: #fff;
  border-radius: 14px;
  overflow: hidden;
  border: 1px solid #e8edf2;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.06);
}

.tab-bar {
  display: flex;
  border-bottom: 1px solid #f1f5f9;
}

.tab-btn {
  flex: 1;
  padding: 12px 8px;
  background: none;
  border: none;
  font-size: 0.8rem;
  font-weight: 600;
  color: #94a3b8;
  cursor: pointer;
  border-bottom: 2px solid transparent;
  transition: color 0.15s, border-color 0.15s;
}
.tab-btn--active {
  color: #1a587b;
  border-bottom-color: #1a587b;
}

.tab-content-wrap { padding: 14px 16px; }

.tab-content-text {
  font-size: 0.875rem;
  color: #475569;
  line-height: 1.65;
  margin: 0;
}

/* ── Options (additions / exclusions) ── */
.options-section {
  background: #fff;
  border-radius: 14px;
  padding: 16px;
  border: 1px solid #e8edf2;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.06);
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.options-section-label {
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1.4px;
  color: #64748b;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 6px;
}

.option-group {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.option-group-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.option-group-name {
  font-size: 0.875rem;
  font-weight: 700;
  color: #0f172a;
}

.option-group-hint {
  font-size: 0.7rem;
  color: #94a3b8;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 6px;
}

.required-chip {
  background: #fee2e2;
  color: #dc2626;
  font-size: 0.6rem;
  font-weight: 700;
  padding: 2px 6px;
  border-radius: 4px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.option-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.option-chip {
  padding: 8px 14px;
  border-radius: 999px;
  border: 1.5px solid #e2e8f0;
  background: #f8fafc;
  color: #475569;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
}
.option-chip:hover { border-color: #1a587b; color: #1a587b; }
.option-chip.active {
  background: #1a587b;
  border-color: #1a587b;
  color: #fff;
}

.option-chip--exclusion:hover { border-color: #dc2626; color: #dc2626; }
.option-chip--exclusion.active {
  background: #dc2626;
  border-color: #dc2626;
  color: #fff;
}

/* ── Quantity ── */
.qty-section {
  background: #fff;
  border-radius: 14px;
  padding: 16px;
  border: 1px solid #e8edf2;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.06);
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.qty-label {
  font-size: 0.875rem;
  font-weight: 700;
  color: #0f172a;
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0;
}

.qty-row {
  display: flex;
  align-items: center;
  gap: 16px;
}

.qty-stepper {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  border: 1.5px solid #e2e8f0;
  background: #f8fafc;
  color: #1a587b;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
}
.qty-stepper:hover { background: #e0f2fe; border-color: #1a587b; }

.qty-value {
  font-size: 1.1rem;
  font-weight: 700;
  color: #0f172a;
  min-width: 24px;
  text-align: center;
}

/* ── Add to cart ── */
.add-to-cart-wrap { padding-top: 4px; }

.add-to-cart-btn {
  width: 100%;
  padding: 15px;
  background: linear-gradient(135deg, #1a587b 0%, #0f3d5c 100%);
  color: #fff;
  border: none;
  border-radius: 14px;
  font-size: 1rem;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  box-shadow: 0 6px 20px rgba(26, 88, 123, 0.35);
  transition: opacity 0.2s, transform 0.1s;
  letter-spacing: 0.3px;
}
.add-to-cart-btn:hover { opacity: 0.92; }
.add-to-cart-btn:active { transform: scale(0.98); }
</style>
